import { Effect } from 'effect';
import type { StudioTool } from '@okrapdf/pdf-studio-plugin/studio';
import { TOOLS } from './data';
import { publicSlugForStudioTool, studioToolIdForSlug } from './studio-tool-id';

/**
 * The one way to search the tool catalogue (W.4.132): the ⌘K palette and the
 * All tools field both go through `matchTools`. The tools are the Studio's, so
 * a name here is the name All tools shows; `/tools/<slug>` pages keep their own
 * names and SEO copy in `data.ts`.
 */

interface PublicPage {
  slug: string;
  name: string;
}

/** Each tool's public pages, so a name learned on a public page still finds it. */
const PUBLIC_PAGES = new Map<string, PublicPage[]>();
for (const { slug, name } of TOOLS) {
  const id = studioToolIdForSlug(slug);
  if (id) {
    const page = { slug, name: name.toLowerCase() };
    PUBLIC_PAGES.set(id, [...(PUBLIC_PAGES.get(id) ?? []), page]);
  }
}

// Ranks, not weights: a hit never outranks a hit of a better kind, so a word in
// a description cannot sit above a tool's name. A tie keeps the catalogue order.
const RANK_EXACT_NAME = 7;
const RANK_NAME_PREFIX = 6;
const RANK_NAME_WORD = 5;
const RANK_NAME_INSIDE = 4;
const RANK_TERM = 3;
const RANK_FUZZY_NAME = 2;
const RANK_DESCRIPTION = 1;

export interface ToolMatch {
  tool: StudioTool;
  score: number;
  /** Matched character offsets in tool.title (empty when the name did not match). */
  nameIndices: number[];
  /**
   * The `/tools/<slug>` page the query lands on, or null for a tool with none.
   * Two pages can share a tool, and each does one format, so `pdf to png` has
   * to reach PDF to PNG rather than the tool's first page.
   */
  publicSlug: string | null;
}

/** Offsets of `q` as a subsequence of `text` (VS Code-style), or null. */
function subsequence(q: string, text: string): number[] | null {
  const indices: number[] = [];
  for (let ti = 0; ti < text.length && indices.length < q.length; ti += 1) {
    if (text[ti] === q[indices.length]) indices.push(ti);
  }
  return indices.length === q.length ? indices : null;
}

function nameRank(q: string, name: string, at: number): number {
  if (name === q) return RANK_EXACT_NAME;
  if (at === 0) return RANK_NAME_PREFIX;
  return /[\s\-/]/.test(name[at - 1]) ? RANK_NAME_WORD : RANK_NAME_INSIDE;
}

function matchTool(q: string, tool: StudioTool): ToolMatch | null {
  const pages = PUBLIC_PAGES.get(tool.id) ?? [];
  const namedPage = pages.find((page) => page.name.includes(q) || page.slug.includes(q));
  const loosePage = namedPage ?? pages.find((page) => subsequence(q, page.name));
  const publicSlug = loosePage?.slug ?? publicSlugForStudioTool(tool.id);

  const name = tool.title.toLowerCase();
  const at = name.indexOf(q);
  if (at >= 0) {
    const nameIndices = Array.from(q, (_, offset) => at + offset);
    return { tool, score: nameRank(q, name, at), nameIndices, publicSlug };
  }
  const terms = [tool.id, ...tool.keywords];
  if (namedPage || terms.some((term) => term.toLowerCase().includes(q))) {
    return { tool, score: RANK_TERM, nameIndices: [], publicSlug };
  }
  const fuzzy = subsequence(q, name);
  if (fuzzy) return { tool, score: RANK_FUZZY_NAME, nameIndices: fuzzy, publicSlug };
  if (loosePage) return { tool, score: RANK_FUZZY_NAME, nameIndices: [], publicSlug };
  // A plain substring: a subsequence of a whole sentence matches nearly anything.
  if (tool.description.toLowerCase().includes(q)) {
    return { tool, score: RANK_DESCRIPTION, nameIndices: [], publicSlug };
  }
  return null;
}

/** Rank tools for a query. The ⌘K palette and the All tools field both search through this. */
export const matchToolsEffect = Effect.fn('web.toolCatalogue.match')(function* (
  query: string,
  tools: StudioTool[],
) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return tools
    .flatMap((tool) => matchTool(q, tool) ?? [])
    .sort((a, b) => b.score - a.score);
});

export function matchTools(query: string, tools: StudioTool[]): ToolMatch[] {
  return Effect.runSync(matchToolsEffect(query, tools));
}
